"use client";
import { Room, RoomEvent } from "livekit-client";
import { UserListKey } from "@shared/constants/constant";
import { useAppSelector } from "@client/store/store.hooks";
import { useState, useCallback, useRef, useEffect } from "react";
import { getMeetingUserList } from "@client/actions/participant/participant.actions";
import { Logger } from "@shared/utils/logger";

/**
 * Participant data from API.
 */
export interface ParticipantData {
  id: number;
  roomId: string;
  userId: number | null;
  name: string | null;
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  isRuntime: boolean;
  joinTime: number;
  leaveTime: number;
  totalTime: number; // accumulated seconds in meeting
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Pending participant data from API.
 */
export interface PendingParticipantData {
  id: number;
  roomId: string;
  userId: number;
  guestId?: string | null;
  isGuest?: boolean;
  name: string | null;
  userName: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  isRejected: boolean;
  isKickedOut?: boolean; // Added to track previously kicked-out users
  createdAt: Date;
  updatedAt: Date;
}

export interface UseParticipantsListReturn {
  participants: ParticipantData[];
  pendingParticipants: PendingParticipantData[];
  isLoading: boolean;
  isRefreshing: boolean;
  error: string | null;
  roomId: string;
  hostId: number;
  isHost: boolean;
  canModerate: boolean;
  hasLoadedOnce: boolean;
  fetchParticipants: (searchText?: string) => Promise<void>;
  fetchPendingParticipants: (searchText?: string) => Promise<void>;
  refreshData: (searchText?: string, force?: boolean) => Promise<void>;
  removePendingParticipant: (userId: number | null, guestId?: string | null) => void;
  prefetchPendingParticipants: () => Promise<void>; /** Silently fetch pending participants without showing loader (for preloading on join-request) */
}

/**
 * Manages participants list with lazy-loading, caching, and smart refreshing.
 * - Fetches approved and pending users.
 * - Uses stale-while-revalidate strategy.
 */
export const useParticipantsList = (room?: Room | null): UseParticipantsListReturn => {
  const connectionDetails = useAppSelector((state) => state.connection.connectionDetails);
  const roomId = connectionDetails?.roomId || "";
  const hostId = connectionDetails?.hostId || 0;
  const isHost = connectionDetails?.isHost || false;
  const canModerate = connectionDetails?.canModerate ?? isHost;

  const [participants, setParticipants] = useState<ParticipantData[]>([]);
  const [pendingParticipants, setPendingParticipants] = useState<PendingParticipantData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track if data has been loaded at least once (for smart loading states)
  const hasLoadedOnceRef = useRef(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Cache to prevent redundant fetches
  const lastFetchRef = useRef<{ time: number; searchText: string }>({ time: 0, searchText: "" });

  /**
   * Fetch all participants (approved/runtime users)
   */
  const fetchParticipants = useCallback(
    async (searchText: string = "") => {
      try {
        setError(null);
        const data = await getMeetingUserList(roomId, UserListKey.ALL_PARTICIPANTS, searchText);

        if (!data.status) {
          throw new Error(data.message || "Failed to fetch participants");
        }

        const participantsList = data.data?.[UserListKey.ALL_PARTICIPANTS] || [];
        setParticipants(participantsList);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch participants";
        setError(errorMessage);
        Logger.error("useParticipantsList", "Fetch participants error:", err);
      }
    },
    [roomId]
  );

  /**
   * Fetch pending participants (only for hosts)
   */
  const fetchPendingParticipants = useCallback(
    async (searchText: string = "") => {
      if (!isHost) {
        setPendingParticipants([]);
        return;
      }

      try {
        setError(null);
        const data = await getMeetingUserList(roomId, UserListKey.PENDING_USERS, searchText);

        if (!data.status) {
          throw new Error(data.message || "Failed to fetch pending participants");
        }

        const pendingList = data.data?.[UserListKey.PENDING_USERS] || [];
        setPendingParticipants(pendingList);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : "Failed to fetch pending participants";
        setError(errorMessage);
        Logger.error("useParticipantsList", "Fetch pending participants error:", err);
      }
    },
    [roomId, isHost]
  );

  /**
   * Refresh both lists (with optional search)
   * OPTIMIZED:
   * - Shows skeleton loading ONLY on first load
   * - Shows subtle refreshing indicator on subsequent loads
   * - Skips fetch if same search was done within 500ms (debounce protection)
   */
  const refreshData = useCallback(
    async (searchText: string = "", force: boolean = false) => {
      // Skip if roomId is not available yet
      if (!roomId) return;

      // Debounce protection: skip if same search was done within 500ms (unless forced)
      const now = Date.now();
      if (!force && lastFetchRef.current.searchText === searchText && now - lastFetchRef.current.time < 500) {
        return;
      }
      lastFetchRef.current = { time: now, searchText };

      // Show skeleton only on first load, otherwise show subtle refresh indicator
      if (!hasLoadedOnceRef.current) {
        setIsLoading(true);
      } else {
        setIsRefreshing(true);
      }

      try {
        await Promise.all([fetchParticipants(searchText), fetchPendingParticipants(searchText)]);

        // Mark as loaded once after first successful fetch
        if (!hasLoadedOnceRef.current) {
          hasLoadedOnceRef.current = true;
          setHasLoadedOnce(true);
        }
      } catch (err) {
        Logger.error("useParticipantsList", "Refresh error:", err);
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [roomId, fetchParticipants, fetchPendingParticipants]
  );

  /**
   * Prefetch pending participants silently (no loader).
   * Call when a join-request is received to preload data so host sees users immediately.
   */
  const prefetchPendingParticipants = useCallback(async () => {
    if (!roomId || !isHost) return;

    try {
      const data = await getMeetingUserList(roomId, UserListKey.PENDING_USERS, "");

      if (data.status) {
        const pendingList = data.data?.[UserListKey.PENDING_USERS] || [];
        setPendingParticipants(pendingList);

        // Mark as loaded once so when host opens pending tab, no skeleton shows
        if (!hasLoadedOnceRef.current) {
          hasLoadedOnceRef.current = true;
          setHasLoadedOnce(true);
        }
      }
    } catch (err) {
      Logger.error("useParticipantsList", "Prefetch pending participants error:", err);
    }
  }, [roomId, isHost]);

  /**
   * Real-time updates via socket.
   * - For join-request: Silently prefetch pending participants (no loader)
   * - For participant-list-updated: Force refresh with loader
   */
  useEffect(() => {
    if (!room) return;

    const handleData = (payload: Uint8Array) => {
      try {
        const str = new TextDecoder().decode(payload);
        const data = JSON.parse(str);

        if (data.type === "join-request") {
          // Silently prefetch pending participants (no loader)
          // Small delay to allow DB writes to propagate
          setTimeout(() => {
            prefetchPendingParticipants();
          }, 500);
        } else if (data.type === "participant-list-updated") {
          // Force refresh for other updates
          setTimeout(() => {
            refreshData("", true);
          }, 500);
        }
      } catch (_e) {
        Logger.error("useParticipantsList", "Data received error:", _e);
      }
    };

    room.on(RoomEvent.DataReceived, handleData);
    return () => {
      room.off(RoomEvent.DataReceived, handleData);
    };
  }, [room, refreshData, prefetchPendingParticipants]);

  // NO automatic fetch on mount - data is fetched lazily when sidebar opens

  return {
    participants,
    pendingParticipants,
    isLoading,
    isRefreshing,
    error,
    roomId,
    hostId,
    isHost,
    canModerate,
    hasLoadedOnce,
    fetchParticipants,
    fetchPendingParticipants,
    refreshData,
    removePendingParticipant: (userId: number | null, guestId?: string | null) => {
      setPendingParticipants((prev) =>
        prev.filter((p) => {
          if (guestId && p.guestId) {
            return p.guestId !== guestId;
          }
          if (userId) {
            return p.userId !== userId;
          }
          return true;
        })
      );
    },
    prefetchPendingParticipants
  };
};
